<template>
  <div>
    <SpecialOccasionBanner :locale="restaurantLocaleCode" />
    <div class="splash-v2-container">
      <SplashHeader :cover="restaurantCover" :fill="splashBackgroundColor" />
      <TopButtons show-logo />
      <div
        class="d-flex flex-column justify-center align-center text-center px-6 flex-grow-1"
      >
        <Avatar
          :photo-url="restaurantLogo"
          :lazy-src="restaurantLogo"
          data-cy="restaurant-logo"
          :size="144"
          :border-width="8"
        />
        <h2 class="mt-2 restaurant-name" data-cy="restaurant-name">
          {{ restaurantName }}
        </h2>
        <p
          v-if="restaurantDescription"
          class="mt-2 mb-0 tp-text-body restaurant-description"
          data-cy="restaurant-description"
        >
          {{ $t(restaurantDescription) }}
        </p>
        <SocialLinks :social-links="socialLinks" class="mt-2" />
        <div class="table-divider w-100 mt-6">
          <v-divider />
          <template v-if="tableName">
            <span class="table-divider__label">{{ tableName }}</span>
            <v-divider />
          </template>
        </div>
        <div class="cta-list w-100 mt-4">
          <template v-for="(action, index) in actions">
            <v-divider
              v-if="action.separated && index > 0"
              :key="`divider-${index}`"
            />
            <button
              :key="index"
              type="button"
              :data-cy="action.dataCy"
              class="cta-row"
              @click="action.action"
            >
              <div class="cta-row__icon">
                <CtaIcon :name="action.icon" />
              </div>
              <div class="cta-row__title">
                {{ action.title }}
              </div>
              <v-progress-circular
                v-if="action.loading"
                indeterminate
                :size="15"
                :width="2"
                class="mr-1"
              />
              <svg
                v-show="!action.hideChevron"
                class="cta-row__chevron"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#737373"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </template>
        </div>
        <div class="splash-footer mt-auto pt-6 pb-4">
          <span class="splash-footer__text">{{ $t('generic.easyWith') }}</span>
          <GreetLogo :width="37.2" :height="12" />
        </div>
      </div>
      <ReviewDrawer ref="reviewDrawer" />
      <CallWaiterDrawer ref="callWaiterDrawer" />
    </div>
  </div>
</template>

<script>
import compact from 'lodash/compact';

import SplashHeader from '@/components/SplashScreen/Header.vue';
import Avatar from '@/components/common/Avatar.vue';
import SocialLinks from '@/components/common/SocialLinks.vue';
import CtaIcon from '@/components/SplashScreen/CtaIcon.vue';
import ReviewDrawer from '@/components/Reviews/ReviewDrawer.vue';
import GreetLogo from '@/components/GreetLogo.vue';
import TopButtons from './SplashScreen/TopButtons.vue';
import SpecialOccasionBanner from './common/SpecialOccasionBanner.vue';
import CallWaiterDrawer from './SplashScreen/CallWaiterDrawer.vue';

export default {
  components: {
    SplashHeader,
    Avatar,
    SocialLinks,
    CtaIcon,
    ReviewDrawer,
    GreetLogo,
    TopButtons,
    SpecialOccasionBanner,
    CallWaiterDrawer,
  },
  props: {
    status: {
      type: String,
      default: 'active',
    },
    failure: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    session() {
      return this.$store.state.session;
    },
    loadingScreenCustomizations() {
      return this.$store.getters.loadingScreenCustomizations;
    },
    restaurantLogo() {
      const loadingScreenCustomizations = this.loadingScreenCustomizations;
      const customSplashScreenLogo = loadingScreenCustomizations.logo;
      const restaurantLogo = this.$store.getters.restaurantLogo;
      const defaultLogo = require('@/assets/images/merchant.svg');

      return restaurantLogo || customSplashScreenLogo || defaultLogo;
    },
    restaurantCover() {
      const splashCover = this.$store.getters.restaurantSplashCover;
      const loadingScreenCustomizations = this.loadingScreenCustomizations;
      const customSplashScreenCover =
        loadingScreenCustomizations.background_image;

      return splashCover || customSplashScreenCover;
    },
    splashBackgroundColor() {
      const loadingScreenCustomizations = this.loadingScreenCustomizations;
      const customSplashScreenColor =
        loadingScreenCustomizations.background_color;

      return customSplashScreenColor;
    },
    restaurantName() {
      return this.$store.getters.restaurantName;
    },
    restaurantDescription() {
      return this.$store.getters.restaurantSplashDescription;
    },
    socialLinks() {
      return this.$store.getters.restaurantSocialLinks;
    },
    tableName() {
      const restaurant = this.$store.getters.restaurant;
      const isRestaurantType = restaurant.type === 'restaurant';
      const table = this.$store.state.table.name;
      const hideTableNumber =
        this.$store.getters.customizationSelectScreenHideTableName;

      if (!table || hideTableNumber) {
        return null;
      }

      if (isRestaurantType) {
        return this.$t('screens.selectScreen.tableName', {
          tableNumber: table,
        });
      }

      return this.$t('screens.selectScreen.tableNameWithoutTable', {
        tableNumber: table,
      });
    },
    hideMenuButton() {
      return this.$store.getters.customizationSelectScreenHideViewMenuButton;
    },
    hideReviewsButton() {
      const hiddenForRestaurantIds = [873];
      const restaurantId = this.$store.getters.getCurrentRestaurantId;
      if (hiddenForRestaurantIds.includes(restaurantId)) {
        return true;
      }
      return this.$store.getters.customizationOrderInfoHideReviews;
    },
    ordersEnabled() {
      return (
        this.$store.getters.functionalityOrderEnabled &&
        !this.$store.getters.ordersTemporarilyDisabled
      );
    },
    viewMenuButtonTitle() {
      if (this.$store.getters.customizationSelectScreenViewMenuText) {
        return this.$t(
          this.$store.getters.customizationSelectScreenViewMenuText
        );
      } else if (this.ordersEnabled) {
        return this.$t('screens.selectScreen.viewMenuAndOrderCta');
      } else {
        return this.$t('screens.selectScreen.viewMenuCta');
      }
    },
    viewMenuButtonIcon() {
      if (this.ordersEnabled) {
        return 'order';
      }
      return 'menu';
    },
    callWaiterEnabled() {
      return this.$store.getters.functionalityCallWaiterEnabled;
    },
    isPayGoEnabled() {
      return this.$store.getters.functionalityPayGoEnabled;
    },
    selectScreenButtons() {
      return this.$store.getters.selectScreenButtons;
    },
    showPaymentHistoryButton() {
      return this.$store.getters.functionalityPayGoEnabled;
    },
    restaurantLocaleCode() {
      const localeCode = this.$store.state.restaurant.locale?.locale_code;
      if (!localeCode) {
        return '';
      }
      return localeCode.split('-')[0];
    },
    actions() {
      const menuButton = !this.hideMenuButton && {
        title: this.viewMenuButtonTitle,
        icon: this.viewMenuButtonIcon,
        action: this.navigateToMenu,
        dataCy: 'select-screen-open-menu',
      };
      const callWaiterButton = this.callWaiterEnabled && {
        title: this.$t('screens.selectScreen.callWaiterCta'),
        icon: 'callWaiter',
        dataCy: 'select-screen-call-waiter',
        hideChevron: true,
        separated: true,
        action: () => {
          this.$refs.callWaiterDrawer.toggle();
        },
      };
      const payGoButton = this.isPayGoEnabled && {
        title: this.$t('screens.selectScreen.payBillCta'),
        icon: 'bill',
        action: this.navigateToPayGo,
        dataCy: 'pay-go-split-bill',
      };
      const paymentHistoryButton = this.showPaymentHistoryButton && {
        title: this.$t('screens.selectScreen.paymentsCta'),
        icon: 'history',
        dataCy: 'payment-history-button',
        action: this.navigateToPaymentHistory,
      };
      const leaveReviewButton = !this.hideReviewsButton && {
        title: this.$t('screens.selectScreen.leaveReviewCta'),
        icon: 'review',
        action: () => {
          this.$refs.reviewDrawer.toggle();
        },
      };
      const customButtons = this.selectScreenButtons.map((button) => {
        const tipButton = button.link?.includes('/tip/');
        const icon = tipButton ? 'tip' : button.icon || 'link';
        return {
          title: this.$t(button.text),
          icon,
          dataCy: button.dataCy,
          action: () => this.onCustomButtonClicked(button),
        };
      });

      return compact([
        menuButton,
        payGoButton,
        ...customButtons,
        leaveReviewButton,
        paymentHistoryButton,
        callWaiterButton,
      ]);
    },
  },
  mounted() {
    const dispatch = this.$store.dispatch;
    dispatch('sendAnalyticsEvent', {
      event: 'loading_screen',
    });

    if (this.failure) {
      dispatch('sendAnalyticsEvent', {
        event: 'fail_screen_displayed',
      });
    }

    if (this.isPayGoEnabled) {
      dispatch('fetchOrdersFromPOS');
    }
  },
  methods: {
    navigateToMenu() {
      const session = this.session;

      if (this.$store.getters.restaurantMenuFilters.length > 0) {
        this.$router.push({
          name: 'MenuFilters',
          params: { session },
        });
      } else {
        this.$router.push({
          name: 'Menu',
          params: { session },
        });
      }
    },
    async navigateToPayGo() {
      this.$router.push({
        name: 'Order',
        params: { session: this.session },
      });
    },
    navigateToPaymentHistory: function () {
      this.$router.push({ name: 'PaymentHistory' });
    },
    onCustomButtonClicked(button) {
      if (button.link && button.open_new_tab) {
        return window.open(button.link, '_blank');
      }

      return (window.location = button.link);
    },
  },
};
</script>

<style lang="scss" scoped>
.splash-v2-container {
  background: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
}

.cta-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cta-row {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  text-align: left;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: #f5f5f5;
    color: #262626;
  }

  &__title {
    flex-grow: 1;
    font-family: 'Poppins', sans-serif !important;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #262626;
  }

  &__chevron {
    flex-shrink: 0;
  }
}

.table-divider {
  display: flex;
  align-items: center;
  gap: 16px;

  &__label {
    flex-shrink: 0;
    font-family: 'Poppins', sans-serif !important;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: #757575;
  }
}

.splash-footer {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;

  &__text {
    font-family: 'Poppins', sans-serif !important;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: #262626;
  }
}

.restaurant-name {
  font-family: 'Poppins', sans-serif !important;
  font-weight: 600;
  font-size: 32px;
  line-height: 36px;
  color: #262626;
}

.restaurant-description {
  line-height: 20px;
  color: #262626;
}
</style>
